/*
 * Still Water core behaviours. Everything here enhances markup that already
 * works without JavaScript:
 *   - appointment sheet: a[data-sw-appointment-open] is a real link to the
 *     consultation page; with JS it opens dialog[data-sw-appointment-sheet]
 *   - reveal: [data-sw-reveal] fades in once visible (.is-revealed)
 *   - film: [data-sw-film][data-sw-rate] plays its muted video slowly while it
 *     is on screen in a visible tab, and restarts with a short fade
 *     (.is-breathing) instead of a hard loop cut (controlFilm)
 *   - appearance: [data-sw-appearance-toggle] switches html[data-sw-appearance]
 *     between dark and light and remembers the choice
 *   - tracks: [data-sw-track] arrows scroll a showroom one stage at a time (the
 *     stages scroll natively without JS)
 *   - tabs: [data-sw-tabs] swap the edition figures in place (the first edition
 *     is rendered by Liquid)
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') {
    window.SwCore = api;
    api.autoInit();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const APPEARANCE_KEY = 'sw-appearance';

  /**
   * Which appearance to show: a remembered visitor choice, else the merchant default,
   * where "auto" follows the device. The inline script in layout/theme.liquid mirrors this.
   *
   * @param {string|null|undefined} stored - value saved under localStorage 'sw-appearance'
   * @param {string|null|undefined} preference - merchant setting: 'dark', 'light' or 'auto'
   * @param {boolean} prefersDark - whether the device prefers a dark colour scheme
   * @returns {'dark'|'light'}
   */
  function resolveAppearance(stored, preference, prefersDark) {
    if (stored === 'light' || stored === 'dark') return stored;
    if (preference === 'light') return 'light';
    if (preference === 'auto') return prefersDark ? 'dark' : 'light';
    return 'dark';
  }

  /**
   * Index of the showroom stage an arrow leads to: the current stage is brought into range,
   * then moved one step, never past the first or last stage.
   *
   * @param {number} current - index of the stage nearest the start of the track
   * @param {number} count - number of stages
   * @param {number} direction - 1 for next, -1 for previous
   * @returns {number}
   */
  function nextTrackIndex(current, count, direction) {
    if (!Number.isFinite(current) || !(count > 0)) return 0;
    const last = count - 1;
    const from = Math.min(last, Math.max(0, Math.round(current)));
    return Math.min(last, Math.max(0, from + Math.sign(direction || 0)));
  }

  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const closeMobileDrawers = () => {
    document.querySelectorAll('[data-aq-mobile-drawer][open]').forEach((drawer) => drawer.removeAttribute('open'));
    document.documentElement.classList.remove('aq-nav-open');
  };

  function initAppointmentSheet() {
    const dialog = document.querySelector('dialog[data-sw-appointment-sheet]');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.swReady === 'true') return;
    dialog.dataset.swReady = 'true';
    let lastTrigger = null;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-sw-appointment-open]');
      if (!trigger || event.defaultPrevented) return;
      // Let modified clicks open the consultation page in a new tab.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      lastTrigger = trigger;
      closeMobileDrawers();
      dialog.showModal();
      document.documentElement.classList.add('sw-sheet-open');
    });

    // Clicks on the backdrop target the dialog element itself.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.querySelectorAll('[data-sw-appointment-close]').forEach((control) => {
      control.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('sw-sheet-open');
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    });
  }

  function initReveal(scope) {
    const nodes = Array.from(scope.querySelectorAll('[data-sw-reveal]:not(.is-revealed)'));
    if (!nodes.length) return;
    if (prefersReducedMotion() || inDesignMode() || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    nodes.forEach((node) => observer.observe(node));
  }

  const GESTURES = ['pointerdown', 'keydown', 'touchstart'];

  /**
   * Plays a hero film while it is on screen and its tab is visible, and pauses it otherwise.
   * An interrupted play() (the tab was hidden, the browser paused silent video to save power,
   * the film was scrolled away) is retried the next time the film can be seen; a refused one
   * (autoplay blocked, e.g. low-power mode) shows the still until the visitor's first tap or key.
   *
   * @param {{ dataset: DOMStringMap, classList: DOMTokenList }} film - [data-sw-film]; data-sw-rate is its speed
   * @param {HTMLVideoElement} video
   * @param {{ document: Document, observe: (target: object, onChange: (inView: boolean) => void) => void }} env
   * @returns {void}
   */
  function controlFilm(film, video, env) {
    const doc = env.document;
    const rate = Math.min(1, Math.max(0.25, parseFloat(film.dataset.swRate) || 1));
    let inView = false;
    let waitingForGesture = false;

    const retryOnGesture = () => {
      waitingForGesture = false;
      GESTURES.forEach((type) => doc.removeEventListener(type, retryOnGesture));
      play();
    };

    function play() {
      if (!inView || doc.hidden) return;
      video.playbackRate = rate;
      const attempt = video.play();
      if (!attempt || typeof attempt.catch !== 'function') return;
      attempt.catch((error) => {
        // An interruption is retried on the next visibility or scroll change.
        if (error && error.name === 'AbortError') return;
        film.classList.add('is-still');
        if (waitingForGesture) return;
        waitingForGesture = true;
        GESTURES.forEach((type) => doc.addEventListener(type, retryOnGesture, { passive: true }));
      });
    }

    video.addEventListener('loadedmetadata', () => {
      video.playbackRate = rate;
    });
    video.addEventListener('play', () => {
      video.playbackRate = rate;
      film.classList.remove('is-still');
    });
    video.addEventListener('timeupdate', () => {
      if (video.duration && video.duration - video.currentTime < 0.4) film.classList.add('is-breathing');
    });
    video.addEventListener('ended', () => {
      video.currentTime = 0;
      play();
      setTimeout(() => film.classList.remove('is-breathing'), 80);
    });
    doc.addEventListener('visibilitychange', () => (doc.hidden ? video.pause() : play()));
    env.observe(film, (visible) => {
      inView = visible;
      if (visible) play();
      else video.pause();
    });
  }

  function initFilm(scope) {
    scope.querySelectorAll('[data-sw-film]').forEach((film) => {
      const video = film.querySelector('video');
      if (!video || video.dataset.swReady === 'true') return;
      video.dataset.swReady = 'true';
      video.muted = true;
      video.loop = false;
      video.playsInline = true;
      video.removeAttribute('controls');

      if (prefersReducedMotion()) {
        video.removeAttribute('autoplay');
        video.pause();
        film.classList.add('is-still');
        return;
      }

      controlFilm(film, video, {
        document,
        observe: (target, onChange) => {
          if (!('IntersectionObserver' in window)) {
            onChange(true);
            return;
          }
          new IntersectionObserver((entries) => entries.forEach((entry) => onChange(entry.isIntersecting)), {
            threshold: 0.1,
          }).observe(target);
        },
      });
    });
  }

  // Showroom tracks: [data-sw-track] holds a [data-sw-track-scroller] of stages and the
  // [data-sw-track-prev] / [data-sw-track-next] buttons, which move one stage at a time.
  function initTracks(scope) {
    scope.querySelectorAll('[data-sw-track]').forEach((track) => {
      if (track.dataset.swReady === 'true') return;
      const scroller = track.querySelector('[data-sw-track-scroller]');
      const prev = track.querySelector('[data-sw-track-prev]');
      const next = track.querySelector('[data-sw-track-next]');
      if (!scroller) return;
      track.dataset.swReady = 'true';
      const stages = () => Array.from(scroller.children);

      const currentIndex = () => {
        const start = scroller.getBoundingClientRect().left + (parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0);
        let nearest = 0;
        let distance = Infinity;
        stages().forEach((stage, index) => {
          const gap = Math.abs(stage.getBoundingClientRect().left - start);
          if (gap < distance) {
            distance = gap;
            nearest = index;
          }
        });
        return nearest;
      };

      const syncButtons = () => {
        const index = currentIndex();
        const last = stages().length - 1;
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= last;
      };

      const go = (direction) => {
        const target = stages()[nextTrackIndex(currentIndex(), stages().length, direction)];
        if (!target) return;
        scroller.scrollTo({
          left: target.offsetLeft - (parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      };

      if (prev) prev.addEventListener('click', () => go(-1));
      if (next) next.addEventListener('click', () => go(1));
      let frame = 0;
      scroller.addEventListener(
        'scroll',
        () => {
          if (frame) return;
          frame = window.requestAnimationFrame(() => {
            frame = 0;
            syncButtons();
          });
        },
        { passive: true }
      );
      syncButtons();
    });
  }

  // Edition tabs: [data-sw-tabs] buttons carry data-sw-tab="n"; every [data-sw-tab-value]
  // element inside the same section shows its data-sw-value-n.
  function initTabs(scope) {
    scope.querySelectorAll('[data-sw-tabs]').forEach((tabs) => {
      if (tabs.dataset.swReady === 'true') return;
      tabs.dataset.swReady = 'true';
      const section = tabs.closest('[data-sw-tab-scope]') || tabs.parentElement;
      tabs.querySelectorAll('[data-sw-tab]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = button.dataset.swTab;
          tabs.querySelectorAll('[data-sw-tab]').forEach((other) => {
            other.setAttribute('aria-pressed', String(other === button));
          });
          section.querySelectorAll('[data-sw-tab-value]').forEach((value) => {
            const text = value.getAttribute(`data-sw-value-${index}`);
            if (text !== null) value.textContent = text;
          });
        });
      });
    });
  }

  // Each switch shows the appearance it leads to ("Light" while dark) and names the action.
  function syncAppearanceToggles(appearance) {
    const other = appearance === 'dark' ? 'light' : 'dark';
    const suffix = other === 'light' ? 'Light' : 'Dark';
    document.querySelectorAll('[data-sw-appearance-toggle]').forEach((button) => {
      const label = button.querySelector('[data-sw-appearance-label]');
      if (label && label.dataset[`label${suffix}`]) label.textContent = label.dataset[`label${suffix}`];
      if (button.dataset[`switch${suffix}`]) button.setAttribute('aria-label', button.dataset[`switch${suffix}`]);
    });
  }

  function initAppearance(scope) {
    const html = document.documentElement;
    // Legacy templates are locked to light until they migrate; their switch is hidden by CSS.
    if (html.hasAttribute('data-sw-appearance-locked')) return;
    // The head script normally resolves "auto" before paint; this covers pages where it did not run.
    if (html.dataset.swAppearance !== 'light' && html.dataset.swAppearance !== 'dark') {
      let stored = null;
      try {
        stored = localStorage.getItem(APPEARANCE_KEY);
      } catch (error) {
        // Storage can be blocked (private modes, site-data settings): fall back to the default.
      }
      const prefersDark =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
      html.dataset.swAppearance = resolveAppearance(stored, html.dataset.swAppearance, prefersDark);
    }

    scope.querySelectorAll('[data-sw-appearance-toggle]').forEach((button) => {
      if (button.dataset.swReady === 'true') return;
      button.dataset.swReady = 'true';
      button.addEventListener('click', () => {
        const next = html.dataset.swAppearance === 'dark' ? 'light' : 'dark';
        html.dataset.swAppearance = next;
        try {
          localStorage.setItem(APPEARANCE_KEY, next);
        } catch (error) {
          // Storage blocked: the switch still applies to this page view.
        }
        syncAppearanceToggles(next);
      });
    });
    syncAppearanceToggles(html.dataset.swAppearance);
  }

  /**
   * Initialises every behaviour inside a scope (the document or a re-rendered section).
   *
   * @param {ParentNode} [scope=document]
   * @returns {void}
   */
  function init(scope) {
    const root = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    // Cancels the CSS reveal fallback in sw-core.css: JS is now in charge of revealing.
    document.documentElement.classList.add('sw-ready');
    initAppearance(root);
    initTracks(root);
    initTabs(root);
    initAppointmentSheet();
    initReveal(root);
    initFilm(root);
  }

  function autoInit() {
    if (typeof document === 'undefined') return;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
    else init(document);
    document.addEventListener('shopify:section:load', (event) => init(event.target));
  }

  return { resolveAppearance, nextTrackIndex, controlFilm, init, autoInit };
});
